import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Summary from '../organisms/Origination/Summary/Summary';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  balanceInfo,
  clientData,
  indicator,
  type SgrCatalogItem,
  type applicationData,
  type commentData,
} from '../types/interfaces';
import Card from '../components/card/Card';
import Tabs from '../components/tabs/Tabs';
import SummaryDocuments from '../organisms/Origination/Summary/Documents';
import SummaryAmounts from '../organisms/common/SummaryAmounts';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import { Toast, useToast } from '../components/toast/toast';
import {
  getApplication,
  updateApplication,
  postCommitteeApproval,
  postSendCommitteeToInstrumentation,
  getSgrCatalog,
} from '../services/ApplicationService';
import useCommitteeMode from '../hooks/useCommitteeMode';
import {
  capitalize,
  hasData,
  hasError,
  isSuccessfulResponse,
  getEnabledRiskCodes,
  getRolesFromToken,
} from '../utils/Utils';
import {
  canPerformCommitteeApprovalActions,
  canPerformCommitteeDeclineActions,
  canViewCommitteeDetail,
  shouldHideCommitteeDeclineOption,
  shouldShowCommitteeApprovalButtons,
  shouldShowCommitteeDeclineButtons,
} from '../utils/committeeMemberRole';
import { buildCommitteeApprovalNextStepCopy } from '../utils/committeeApprovalModalCopy';
import { resolveSolicitudProducts } from '../utils/solicitudProducts';
import { APPLICATION_STATUS, RISK_CODES } from '../utils/Constants';
import {
  getApplicationStatusKey,
  isFinishedStatus,
} from '../utils/applicationStatusUtils';
import Modal from '../components/modal/modal';
import P from '../components/texts/p/P';
import Span from '../components/texts/span/Span';
import TextArea from '../components/textarea/TextArea';
import { initialClientData } from '../utils/Constants';
import Icon from '../components/icon/Icon';
import H from '../components/texts/h/H';
import Application from '../organisms/Origination/Application';
import useRole from '../hooks/useRole';
import {
  DASHBOARD_KEYS,
  navigateAfterFlowAction,
  navigateBackToList,
  resolveListRoute,
  type DetailLocationState,
} from '../utils/listNavigationState';
import useStatus from '../hooks/useStatus';
import { getBalanceInfoByCode, getBalanceSheetsByCuit, getDebts } from '../services/RiskService';
import CheckLightResume from '../organisms/Risk/CheckLight/ChecklightResume';
import LidecoSemaforo from '../organisms/Risk/CheckLight/LidecoSemaforo';
import { hasCommitteeRiskTabContent } from '../utils/committeeRiskTab';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { normalizeNetWorthForDisplay } from '../utils/BalanceDataUtils';
import { useClientTheme } from '../context/ThemeContext';
import RiskAnalysisPdf from '../organisms/Risk/Pdf/RiskAnalysisPdf';
import { useRiskPdf } from '../hooks/useRiskPdf';
import { useReferrerNames } from '../hooks/useReferrerNames';
import { useOfficers } from '../hooks/useOfficers';
import ApprovalHistory from '../organisms/Committee/ApprovalHistory';
import DeclineOrRequestChangesModal from '../components/modal/DeclineOrRequestChangesModal';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';
import RiskCreditoMaximoReadOnly from '../organisms/Risk/CreditoMaximo/RiskCreditoMaximoReadOnly';
import { useCommitteeCreditoMaximo } from '../hooks/useCommitteeCreditoMaximo';
import type { ProductoOtorgado } from '../organisms/Risk/CreditoMaximo/CreditoMaximo.types';
import { extractPreviousBpbIndicators } from '../utils/extractPreviousBpbIndicators';
import {
  mapApplicationProductToApprovedProduct,
  mapProductoOtorgadoToApprovedProduct,
} from '../utils/committeeApprovedProducts';
import {
  getCommitteeCommentMaxLength,
} from '../utils/committeeApprovalComment';
import { useManagements } from '../hooks/useManagements';
import MultiSgrApprovalModal, {
  type MultiSgrApprovalSubmitResult,
} from '../organisms/Committee/MultiSgrApprovalModal';
import SgrApprovalDistributionReadOnly from '../organisms/Committee/SgrApprovalDistributionReadOnly';
import type { MultiSgrBaseProduct } from '../services/ApplicationService';

const Committee = () => {
  const appConfig = useConfig();
  const fiscalId = useFiscalIdConfig();
  const isUruguayEnabled = appConfig.isUruguayEnabled;
  const localCurrencyCode = resolveLocalCurrencyCode(appConfig);
  const isInstrumentationEnabled = appConfig.isInstrumentationEnabled ?? true;
  const isMultipleApprovalInCommitteeEnabled =
    appConfig.isMultipleApprovalInCommitteeEnabled ?? false;
  const isCommitteeMemberQuorumEnabled =
    appConfig.isCommitteeMemberQuorumEnabled ?? false;
  const isMaxCreditCalculatorEnabled = appConfig.isMaxCreditCalculatorEnabled === true;
  const isQuotationsEnabled = appConfig.isQuotationsEnabled === true;
  const isMultiSgrApprovalEnabled = appConfig.isMultiSgrApprovalEnabled === true;
  const committeeCommentMaxLength = getCommitteeCommentMaxLength(appConfig);
  const { cuit, id } = useParams();
  const location = useLocation();
  const detailState = (location.state ?? {}) as DetailLocationState;
  const status = detailState.status;
  const statusCode = detailState.statusCode;
  const listState = detailState.listState;
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigateBackToList(
      navigate,
      listState,
      resolveListRoute(DASHBOARD_KEYS.committee),
    );
  };
  const { clientTheme } = useClientTheme();
  const { referrerNames } = useReferrerNames();
  const { officers } = useOfficers();
  const [isClientDataLoaded, setIsClientDataLoaded] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientData>(initialClientData);
  const [committeeCreditoMaximoRefreshKey, setCommitteeCreditoMaximoRefreshKey] = useState(0);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const [isSaving, _setIsSaving] = useState<boolean>(false);
  const { isComite, isCommercial, isInstrumentator, isAdmin, isRiskAnalyst, isCommitteeOnly } = useRole();
  const { isRejected, isFinished } = useStatus({
    applicationStatus: statusCode ?? status ?? '',
  });
  const [isSendApplicationModal, setIsSendApplicationModal] =
    useState<boolean>(false);
  const [isMultipleApprovalModal, setIsMultipleApprovalModal] =
    useState<boolean>(false);
  const [approvalDescription, setApprovalDescription] = useState<string>('');
  const [applicationData, setApplicationData] = useState<any>({});
  const [riskData, setRiskData] = useState<any>([]);
  const [previousBpbIndicators, setPreviousBpbIndicators] = useState<
    indicator[] | undefined
  >(undefined);
  const [debts, setDebts] = useState<any>({});
  const [balanceInfo, setBalanceInfo] = useState<balanceInfo | undefined>(
    undefined
  );
  const [previousBalanceInfo, setPreviousBalanceInfo] = useState<balanceInfo | undefined>(
    undefined
  );
  const showDualBalance = appConfig.isShowDualBalanceEnabled ?? false;
  const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
  const { managements } = useManagements(isManagementSelectionEnabled);
  const [postBalanceData, setPostBalanceData] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSendToInstrumentationModal, setIsSendToInstrumentationModal] =
    useState<boolean>(false);
  const [isMultiSgrApprovalModal, setIsMultiSgrApprovalModal] =
    useState<boolean>(false);
  const [sgrCatalog, setSgrCatalog] = useState<SgrCatalogItem[]>([]);
  /**
   * Task 50111: en modo MEMBER_QUORUM (Innova) el front no debe disparar el cambio
   * de estado a INS por sí mismo; sólo invoca POST .../committee/send-to-instrumentation
   * y refresca. La fuente de verdad sobre quórum y permisos es ms-application.
   */
  const {
    approvalData: committeeApprovalData,
    isMemberQuorumMode,
    quorum,
    canApprove,
    canSendToInstrumentation,
    refresh: refreshCommitteeMode,
  } = useCommitteeMode({
    cuit,
    applicationId: id,
    enabled: !isRejected && !isFinished,
  });
  const pdfRef = useRef<HTMLDivElement>(null);
  const { isGenerating: isPdfGenerating, generatePdfFromNode } = useRiskPdf({
    fileName: `analisis_riesgo_${cuit || 'cliente'}.pdf`,
    useImprovedPdfExport: showDualBalance,
  });

  useEffect(() => {
    setPreviousBpbIndicators(extractPreviousBpbIndicators(riskData));
  }, [riskData]);

  const noopCredito = useCallback(() => {}, []);
  const noopProductoCredito = useCallback(
    (_productId: number, _field: 'cantidad_otorgada' | 'monto_otorgado', _value: string) => {},
    []
  );

  const onCommitteeClientDataChange = useCallback((e: clientData) => {
    setClientData(e);
    setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
  }, []);

  /** Evita getCreditoMaximo antes del getApplication del Comité. No usar `clientData.id`: el API puede devolver id numérico y `id?.trim` queda siempre falsy. */
  const committeeApplicationReady = isClientDataLoaded;

  const {
    state: committeeCreditoState,
    productLines: committeeProductLines,
    availableAssets: committeeAvailableAssets,
    loading: committeeCreditoLoading,
    superaMaximo: committeeSuperaMaximo,
  } = useCommitteeCreditoMaximo(
    cuit,
    id,
    isMaxCreditCalculatorEnabled,
    clientData.products,
    isQuotationsEnabled,
    committeeCreditoMaximoRefreshKey,
    committeeApplicationReady
  );

  const currentStatus = useMemo(() => {
    if (statusCode) return getApplicationStatusKey(statusCode);
    if (clientData?.status?.code) {
      return getApplicationStatusKey(clientData.status);
    }
    if (status !== undefined && status !== null && status !== '') {
      return getApplicationStatusKey(status);
    }
    if (clientData?.status?.id) {
      return getApplicationStatusKey(clientData.status);
    }
    return '';
  }, [status, statusCode, clientData?.status]);

  const committeeSolicitudClientData = useMemo(
    () => ({
      ...clientData,
      products: resolveSolicitudProducts(clientData),
    }),
    [clientData]
  );

  const multiSgrBaseProducts = useMemo((): MultiSgrBaseProduct[] => {
    if (
      isMaxCreditCalculatorEnabled &&
      committeeCreditoState?.productos &&
      committeeCreditoState.productos.length > 0
    ) {
      return committeeCreditoState.productos.map((p: ProductoOtorgado) => {
        const raw = p.monto_otorgado;
        const amount = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
        return {
          product_line_code: p.product_line_code,
          currency_code: p.currency_code,
          amount: isNaN(amount) ? 0 : amount,
        };
      });
    }
    return clientData.products.map((item: applicationData) => {
      const raw = item.amount.value;
      const amount = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
      return {
        product_line_code: item.product_line_code,
        currency_code: item.amount.currency_code,
        amount: isNaN(amount) ? 0 : amount,
      };
    });
  }, [
    isMaxCreditCalculatorEnabled,
    committeeCreditoState?.productos,
    clientData.products,
  ]);

  /**
   * En LEGACY antiguo (sin `isMultipleApprovalInCommitteeEnabled`) los botones se
   * mostraban siempre — preservar ese comportamiento. En LEGACY multi-step y en
   * MEMBER_QUORUM, `useCommitteeMode.canApprove` ya encapsula el chequeo correcto
   * (rol del user vs roles pendientes / quórum + userAlreadyApproved).
   *
   * Nota: el botón "Enviar a instrumentar" del modo MEMBER_QUORUM se renderiza
   * aparte y depende de `canSendToInstrumentation`, no de este flag.
   */
  const committeeViewerFlags = useMemo(
    () => ({ isComite, isCommercial, isInstrumentator, isAdmin, isRiskAnalyst, isCommitteeOnly }),
    [isComite, isCommercial, isInstrumentator, isAdmin, isRiskAnalyst, isCommitteeOnly],
  );

  const canViewCommitteeFolder = useMemo(
    () => canViewCommitteeDetail(committeeViewerFlags),
    [committeeViewerFlags],
  );

  const canPerformCommitteeActions = useMemo(
    () =>
      canPerformCommitteeApprovalActions(
        isMemberQuorumMode,
        getRolesFromToken(),
        committeeViewerFlags,
      ),
    [isMemberQuorumMode, committeeViewerFlags],
  );

  const canPerformCommitteeDecline = useMemo(
    () =>
      canPerformCommitteeDeclineActions(
        isMemberQuorumMode,
        getRolesFromToken(),
        committeeViewerFlags,
      ),
    [isMemberQuorumMode, committeeViewerFlags],
  );

  const committeeButtonVisibility = useMemo(
    () => ({
      isMultipleApprovalInCommitteeEnabled,
      isCommitteeMemberQuorumEnabled,
      canApprove,
      canPerformCommitteeDecline,
    }),
    [
      isMultipleApprovalInCommitteeEnabled,
      isCommitteeMemberQuorumEnabled,
      canApprove,
      canPerformCommitteeDecline,
    ],
  );

  const shouldShowApprovalButtons = useMemo(
    () => shouldShowCommitteeApprovalButtons(committeeButtonVisibility),
    [committeeButtonVisibility],
  );

  const shouldShowDeclineButtons = useMemo(
    () => shouldShowCommitteeDeclineButtons(committeeButtonVisibility),
    [committeeButtonVisibility],
  );

  useEffect(() => {
    if (cuit && id) {
      setIsLoading(true);
      setIsClientDataLoaded(false);
      getApplication(cuit, id).then((response) => {
        if (hasData(response)) {
          setClientData(response.data);
          setIsClientDataLoaded(true);
          setPostBalanceData(response.data.post_balance_sheet_items);
          getBalanceInfoFromCode(cuit, response.data.balance_sheet_code);

          // Fetch previous balance if dual balance mode is enabled
          if (showDualBalance && response.data.balance_sheet_code) {
            getPreviousBalanceForDualView(cuit, response.data.balance_sheet_code);
          }

          if (response.data.risk_analysis) {
            const risks = response.data.risk_analysis.risks;
            setRiskData(risks);
            setPreviousBpbIndicators(extractPreviousBpbIndicators(risks));
          } else {
            setRiskData([]);
            setPreviousBpbIndicators(undefined);
          }
          const code = response.data.client_debt_analysis_code;
          getDebts(cuit, code).then((response) => {
            if (hasData(response) && response.status === 200) {
              setDebts(response.data);
            } else if (hasError(response).isAnyError) {
              useToast.error({
                title: 'Ocurrió un error al consultar la información.',
                id: 'risk-toast',
                description: hasError(response).isClientError
                  ? 'Error del cliente'
                  : 'Error del servidor',
                closeButton: true,
                autoClose: 5000,
              });
            }
          });
        }
        setIsLoading(false);
      });
    }
  }, [cuit, id, showDualBalance]);

  const getBalanceInfoFromCode = (cuit: string, balanceSheetCode: string) => {
    getBalanceInfoByCode(cuit, balanceSheetCode).then((response) => {
      if (hasData(response) && response.status === 200) {
        const formatted = { ...response.data, ...formatNumbers(response.data) };
        setBalanceInfo(normalizeNetWorthForDisplay(formatted));
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error al consultar la información del balance.',
          id: 'risk-toast',
          description: hasError(response).isClientError
            ? 'Error del cliente'
            : 'Error del servidor',
          closeButton: true,
          autoClose: 5000,
        });
      }
    });
  };

  // Fetch the previous balance for dual balance mode using balance code
  const getPreviousBalanceForDualView = (cuit: string, currentBalanceCode: string) => {
    getBalanceSheetsByCuit(cuit).then((response) => {
      if (hasData(response) && response.status === 200) {
        // Find the current balance by code
        const currentIndex = response.data.findIndex(
          (balance: { code: string }) => balance.code === currentBalanceCode
        );
        
        // If we found the current balance and there's one after it (previous period)
        if (currentIndex !== -1 && response.data.length > currentIndex + 1) {
          const previousBalance = response.data[currentIndex + 1];
          const formatted = { ...previousBalance, ...formatNumbers(previousBalance) };
          setPreviousBalanceInfo(normalizeNetWorthForDisplay(formatted));
        }
      }
    });
  };

  const handleDownloadPdf = useCallback(async () => {
    await generatePdfFromNode(pdfRef.current);
  }, [generatePdfFromNode]);

  const formatNumbers = (obj: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]): [string, any] => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return [key, formatNumbers(value)];
        }
        // Convertir números a strings para que se muestren correctamente en los inputs
        if (typeof value === 'number') {
          return [key, String(value)];
        }
        return [key, value];
      })
    );
  };

  const contentTabs = useMemo(
    () => [
      {
        label: 'Solicitud',
        content: (
          <>
            <Summary
              setClientData={onCommitteeClientDataChange}
              applicationStatus={currentStatus}
              isSaving={isSaving}
              clientData={committeeSolicitudClientData}
              onLoadClientData={(e: boolean) => setIsClientDataLoaded(e)}
              isClientDataLoaded={isClientDataLoaded}
              showButtons={false}
              viewOnly
            />

            {isMaxCreditCalculatorEnabled && cuit && id && (
              <Card margin={{ top: '4' }} width='100%'>
                <RiskCreditoMaximoReadOnly
                  mode='monto-only'
                  loading={committeeCreditoLoading}
                  creditoState={committeeCreditoState}
                  productLines={committeeProductLines}
                  superaMaximo={committeeSuperaMaximo}
                  localCurrencyCode={localCurrencyCode}
                  isUruguayEnabled={isUruguayEnabled}
                  isQuotationsEnabled={isQuotationsEnabled}
                  availableAssets={committeeAvailableAssets}
                  summaryLayout='inline'
                />
              </Card>
            )}

            {isFinishedStatus(currentStatus) &&
              ((clientData.approved_products && clientData.approved_products.length > 0) ||
               (clientData.sgr_distributions && clientData.sgr_distributions.length > 0)) && (
                <Card margin={{ top: '4' }} width='100%'>
                  <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
                    <H
                      type='3'
                      variant='neutral-1'
                      withMargin={false}
                      weight='bold'
                      modifier='strong'
                    >
                      Monto Aprobado
                    </H>
                  </Card>
                  {clientData.approved_products && clientData.approved_products.length > 0 && (
                    <Card margin={{ top: '4' }} width='100%'>
                      <SummaryAmounts
                        clientData={clientData}
                        isManualAmount={false}
                        amounts={clientData.approved_amount || []}
                        products={clientData.approved_products}
                        localCurrencyCode={localCurrencyCode}
                      />
                    </Card>
                  )}
                  {clientData.sgr_distributions &&
                    clientData.sgr_distributions.length > 0 && (
                      <SgrApprovalDistributionReadOnly
                        sgrDistributions={clientData.sgr_distributions}
                      />
                    )}
                </Card>
              )}
          </>
        ),
      },
      {
        label: 'Análisis de riesgo',
        content: (
          <>
            <CheckLightResume
              data={riskData}
              showBalanceInfo={true}
              isLoading={isLoading}
              handleRiskData={(e: any) => setRiskData(e)}
              riskCodes={
                isUruguayEnabled
                  ? [RISK_CODES.bpb]
                  : getEnabledRiskCodes([RISK_CODES.bpb, RISK_CODES.casfog, RISK_CODES.bcra])
              }
              hideArBureauSections={isUruguayEnabled}
              debts={debts}
              documents={clientData.files}
              cuit={cuit}
              hideInfo={true}
              balanceData={balanceInfo}
              postBalanceData={postBalanceData}
              onDownloadPdf={handleDownloadPdf}
              isPdfLoading={isPdfGenerating}
              showDualBalance={showDualBalance}
              previousBalanceData={previousBalanceInfo}
              previousBpbIndicators={previousBpbIndicators}
              analystNotes={clientData.analyst_notes}
              hideNotes
            />
            {isUruguayEnabled && (
              <LidecoSemaforo
                riskData={Array.isArray(riskData) ? riskData : undefined}
                isLoading={isLoading}
                onRefresh={noopCredito}
                hideRefresh
              />
            )}
            {isMaxCreditCalculatorEnabled && cuit && id && (
              <div style={{ marginTop: '32px' }}>
                <RiskCreditoMaximoReadOnly
                  mode='risk-analysis'
                  loading={committeeCreditoLoading}
                  creditoState={committeeCreditoState}
                  productLines={committeeProductLines}
                  superaMaximo={committeeSuperaMaximo}
                  localCurrencyCode={localCurrencyCode}
                  isUruguayEnabled={isUruguayEnabled}
                  isQuotationsEnabled={isQuotationsEnabled}
                  availableAssets={committeeAvailableAssets}
                  solicitudClientData={committeeSolicitudClientData}
                  clientReason={clientData.reason}
                  summaryLayout='inline'
                />
              </div>
            )}
            <Card margin={{ top: '4' }}>
              <Card
                variant={clientTheme ? 'primary' : 'success'}
                modifier='weak'
                spacing={'2'}
              >
                <H
                  type='3'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Notas
                </H>
              </Card>
              <Card margin={{ top: '3' }}>
                <P type='2' variant='neutral' withMargin={false}>
                  {clientData.analyst_notes && clientData.analyst_notes.trim() !== ''
                    ? clientData.analyst_notes
                    : 'sin comentarios'}
                </P>
              </Card>
            </Card>
          </>
        ),
      },
      {
        label: 'Documentación completa',
        content: (
          <SummaryDocuments
            cuit={clientData.cuit}
            documents={clientData.files}
            legalEntity={clientData.basic_data.legal_entity}
            applicationId={id}
            companyName={clientData.basic_data?.company_name}
          />
        ),
      },
      ...(isMultipleApprovalInCommitteeEnabled || isCommitteeMemberQuorumEnabled
        ? [
            {
              label: 'Historial de aprobación',
              content: (
                <ApprovalHistory
                  cuit={cuit || ''}
                  applicationId={id || ''}
                  currentCycleId={quorum?.cycleId}
                />
              ),
            },
          ]
        : []),
    ],
    [
      status,
      isSaving,
      clientData,
      isClientDataLoaded,
      currentStatus,
      clientTheme,
      isMaxCreditCalculatorEnabled,
      isQuotationsEnabled,
      cuit,
      id,
      committeeCreditoLoading,
      committeeCreditoState,
      committeeProductLines,
      committeeAvailableAssets,
      committeeSuperaMaximo,
      onCommitteeClientDataChange,
      noopCredito,
      noopProductoCredito,
      riskData,
      isLoading,
      debts,
      balanceInfo,
      postBalanceData,
      handleDownloadPdf,
      isPdfGenerating,
      showDualBalance,
      previousBalanceInfo,
      previousBpbIndicators,
      isMultipleApprovalInCommitteeEnabled,
      isCommitteeMemberQuorumEnabled,
      isUruguayEnabled,
    ]
  );

  const handleApprove = () => {
    const status = isInstrumentationEnabled 
      ? APPLICATION_STATUS.instrumentation.label 
      : APPLICATION_STATUS.finished.label;

    const useManual = applicationData.manualTotalAmounts === true;

    const formatProductsFromApplication = () =>
      applicationData.applications
        ?.filter((item) => !!item)
        .map((item) => mapApplicationProductToApprovedProduct(item)) || [];

    const formatProductsFromCreditoMaximo = () =>
      committeeCreditoState?.productos?.map((p: ProductoOtorgado) =>
        mapProductoOtorgadoToApprovedProduct(p),
      ) || [];

    const sumAmounts = (source: any[], currency: string): number => {
      return (
        source
          ?.filter((p) => p.currency_code === currency)
          .reduce((sum, p) => {
            const value =
              typeof p.value === 'string' ? parseFloat(p.value) : p.value;
            return sum + (isNaN(value) ? 0 : value);
          }, 0) || 0
      );
    };

    const useGrantedProducts =
      isMaxCreditCalculatorEnabled &&
      committeeCreditoState?.productos &&
      committeeCreditoState.productos.length > 0;

    const products = useGrantedProducts
      ? formatProductsFromCreditoMaximo()
      : formatProductsFromApplication();

    const baseSource = useManual
      ? applicationData.requestedAmounts
      : products.map((p) => ({
          currency_code: p.amount.currency_code,
          value: p.amount.value,
        }));

    const totalARS = parseFloat(sumAmounts(baseSource, localCurrencyCode).toFixed(2));
    const totalUSD = parseFloat(sumAmounts(baseSource, 'USD').toFixed(2));

    const payload = {
      status_code: status,
      comment: modalText,
      approved_products: {
        products,
        manual_approved_total_amount_ars: totalARS,
        manual_approved_total_amount_usd: totalUSD,
      },
    };
    handleApplication(payload);
  };

  const handleApplication = (payload: any) => {
    setIsSendingApplication(true);
    const mapStatus = new Map();
    mapStatus.set(
      APPLICATION_STATUS.rejected.label,
      'Declinaste la solicitud de este cliente.'
    );
    mapStatus.set(
      APPLICATION_STATUS.pending.label,
      'Solicitaste cambios en la solicitud. El comercial responsable será notificado.'
    );
    mapStatus.set(
      APPLICATION_STATUS.instrumentation.label,
      'Aceptaste la documentación. El comercial responsable será notificado.'
    );
    mapStatus.set(
      APPLICATION_STATUS.finished.label,
      'Cerraste la carpeta de este cliente.'
    );

    updateApplication(cuit || '', payload, id || '').then((response) => {
      if (isSuccessfulResponse(response)) {
        useToast.success({
          title: mapStatus.get(payload.status_code),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, detailState);
        }, 1000);
      } else {
        const errorTitle = payload.status_code === APPLICATION_STATUS.finished.label
          ? 'Ocurrió un error al cerrar la carpeta.'
          : 'Ocurrió un error inesperado. Intentá nuevamente.';
        
        useToast.error({
          title: errorTitle,
          id: 'application-toast',
          description: hasError(response).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: true,
          autoClose: 5000,
        });
      }
      setIsSendingApplication(false);
    });
  };

  const handleMultiSgrModalSubmit = (result: MultiSgrApprovalSubmitResult) => {
    setIsMultiSgrApprovalModal(false);
    setIsSendingApplication(true);
    const payload: commentData = {
      status_code: result.statusCode,
      comment: result.comment,
      approved_products: result.approved_products,
      sgr_distributions: result.sgr_distributions,
    };
    updateApplication(cuit || '', payload, id || '').then((updateResponse) => {
      if (isSuccessfulResponse(updateResponse)) {
        setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
        useToast.success({
          title: 'Solicitud aprobada exitosamente.',
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, detailState);
        }, 1000);
      } else {
        useToast.error({
          title: 'Ocurrió un error al aprobar la solicitud.',
          id: 'application-toast',
          description: hasError(updateResponse).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: true,
          autoClose: 5000,
        });
      }
      setIsSendingApplication(false);
    });
  };

  const handleMultipleApproval = () => {
    setIsSendingApplication(true);
    postCommitteeApproval(cuit || '', id || '', approvalDescription, APPLICATION_STATUS.approved.label).then((response) => {
      if (isSuccessfulResponse(response)) {
        /**
         * Task 50111 — MEMBER_QUORUM (Innova): el front NO debe disparar el cambio
         * de estado a INS por sí mismo. Si la aprobación recién registrada alcanza
         * `committeeAutoQuorum`, ms-application empuja la solicitud a INS dentro de
         * la misma transacción. Si solo alcanza `committeeMinManualQuorum`, queda
         * habilitado el botón "Enviar a instrumentar". Refrescamos para reflejar
         * el nuevo estado/quorum y volvemos al dashboard.
         */
        if (isMemberQuorumMode) {
          setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
          refreshCommitteeMode();
          useToast.success({
            title: 'Aprobación registrada.',
            id: 'application-toast',
            description:
              'Tu aprobación fue registrada para esta solicitud.',
            closeButton: false,
            autoClose: 5000,
          });
          setTimeout(() => {
            navigateAfterFlowAction(navigate, location.pathname, detailState);
          }, 1000);
          setIsSendingApplication(false);
          return;
        }

        const approvalPayload = response?.data ?? response;
        // Motor LEGACY ya cerró COM→FIN/INS en el mismo POST (52834 T11 / Engram #554).
        if (approvalPayload?.transitioned === true) {
          setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
          useToast.success({
            title: 'Solicitud aprobada exitosamente.',
            id: 'application-toast',
            description: '',
            closeButton: false,
            autoClose: 5000,
          });
          setTimeout(() => {
            navigateAfterFlowAction(navigate, location.pathname, detailState);
          }, 1000);
          setIsSendingApplication(false);
          return;
        }

        // If hasNext is false, also call the regular approval endpoint
        if (!committeeApprovalData?.hasNext) {
          const runLegacyFinalApproval = () => {
            const status = isInstrumentationEnabled 
              ? APPLICATION_STATUS.instrumentation.label 
              : APPLICATION_STATUS.finished.label;

            // Montos otorgados (paso 4 riesgo / snapshot max_credit) cuando el calculador está activo;
            // si no, usar productos de la solicitud original.
            const products =
              isMaxCreditCalculatorEnabled &&
              committeeCreditoState?.productos &&
              committeeCreditoState.productos.length > 0
                ? committeeCreditoState.productos.map((p: ProductoOtorgado) =>
                    mapProductoOtorgadoToApprovedProduct(p),
                  )
                : clientData.products.map((item: applicationData) =>
                    mapApplicationProductToApprovedProduct(item),
                  );

            const sumAmounts = (source: any[], currency: string): number => {
              return (
                source
                  ?.filter((p: any) => p.currency_code === currency)
                  .reduce((sum: number, p: any) => {
                    const value =
                      typeof p.value === 'string' ? parseFloat(p.value) : p.value;
                    return sum + (isNaN(value) ? 0 : value);
                  }, 0) || 0
              );
            };

            const baseSource = products.map((p: any) => ({
              currency_code: p.amount.currency_code,
              value: p.amount.value,
            }));

            const totalARS = parseFloat(sumAmounts(baseSource, localCurrencyCode).toFixed(2));
            const totalUSD = parseFloat(sumAmounts(baseSource, 'USD').toFixed(2));

            const payload: commentData = {
              status_code: status,
              comment: approvalDescription,
              approved_products: {
                products,
                manual_approved_total_amount_ars: totalARS,
                manual_approved_total_amount_usd: totalUSD,
              },
            };

            updateApplication(cuit || '', payload, id || '').then((updateResponse) => {
              if (isSuccessfulResponse(updateResponse)) {
                setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
                useToast.success({
                  title: 'Solicitud aprobada exitosamente.',
                  id: 'application-toast',
                  description: '',
                  closeButton: false,
                  autoClose: 5000,
                });
                setTimeout(() => {
                  navigateAfterFlowAction(navigate, location.pathname, detailState);
                }, 1000);
              } else {
                useToast.error({
                  title: 'Ocurrió un error al aprobar la solicitud.',
                  id: 'application-toast',
                  description: hasError(updateResponse).isClientError
                    ? 'Error de cliente'
                    : 'Error técnico',
                  closeButton: true,
                  autoClose: 5000,
                });
              }
              setIsSendingApplication(false);
            });
          };

          runLegacyFinalApproval();
          return;
        } else {
          setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
          // If hasNext is true, just show success and return to dashboard
          useToast.success({
            title: 'Solicitud aprobada exitosamente.',
            id: 'application-toast',
            description: 'La solicitud será enviada para su revisión.',
            closeButton: false,
            autoClose: 5000,
          });
          setTimeout(() => {
            navigateAfterFlowAction(navigate, location.pathname, detailState);
          }, 1000);
          setIsSendingApplication(false);
        }
      } else {
        useToast.error({
          title: 'Ocurrió un error al aprobar la solicitud.',
          id: 'application-toast',
          description: hasError(response).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: true,
          autoClose: 5000,
        });
        setIsSendingApplication(false);
      }
    });
  };

  /**
   * Task 50111: en MEMBER_QUORUM también queremos registrar la decisión del miembro
   * en `committee_approval_history` antes de transicionar la solicitud, para que
   * el timeline del ciclo cerrado deje constancia de quién declinó / pidió cambios.
   * Por eso reusamos el flag `isMultipleApprovalInCommitteeEnabled || isMemberQuorumMode`
   * para encadenar `postCommitteeApproval` con `updateApplication`.
   */
  const requiresHistoryRegistration =
    isMultipleApprovalInCommitteeEnabled || isMemberQuorumMode;

  const handleReject = (comment: string = modalText) => {
    setIsRejectApplication(false);

    if (requiresHistoryRegistration) {
      setIsSendingApplication(true);
      postCommitteeApproval(cuit || '', id || '', comment, APPLICATION_STATUS.rejected.label).then((response) => {
        if (isSuccessfulResponse(response)) {
          setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
          handleApplication({
            status_code: APPLICATION_STATUS.rejected.label,
            comment,
          });
        } else {
          useToast.error({
            title: 'Ocurrió un error al declinar la solicitud.',
            id: 'application-toast',
            description: hasError(response).isClientError
              ? 'Error de cliente'
              : 'Error técnico',
            closeButton: true,
            autoClose: 5000,
          });
          setIsSendingApplication(false);
        }
      });
    } else {
      handleApplication({
        status_code: APPLICATION_STATUS.rejected.label,
        comment,
      });
    }
  };

  const handleRequestChanges = (comment: string = modalText) => {
    setIsRejectApplication(false);

    if (requiresHistoryRegistration) {
      setIsSendingApplication(true);
      postCommitteeApproval(cuit || '', id || '', comment, APPLICATION_STATUS.pending.label).then((response) => {
        if (isSuccessfulResponse(response)) {
          setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
          handleApplication({
            status_code: APPLICATION_STATUS.pending.label,
            comment,
          });
        } else {
          useToast.error({
            title: 'Ocurrió un error al solicitar cambios.',
            id: 'application-toast',
            description: hasError(response).isClientError
              ? 'Error de cliente'
              : 'Error técnico',
            closeButton: true,
            autoClose: 5000,
          });
          setIsSendingApplication(false);
        }
      });
    } else {
      handleApplication({
        status_code: APPLICATION_STATUS.pending.label,
        comment,
      });
    }
  };

  const sendApplicationModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          {isInstrumentationEnabled 
            ? 'La solicitud pasará a la instancia de Instrumentación. Podés editar los montos y los productos, y dejar un mensaje para el productor.'
            : 'La carpeta se cerrará. Podés editar los montos y los productos, y dejar un mensaje para el productor.'}
        </P>
        <Card margin={{ top: '3' }}>
          <Application
            cuit={cuit || ''}
            id={String(id)}
            onSave={() => {}}
            applicationStatus={currentStatus}
            onPreviousClick={() => {}}
            isSaving={false}
            manualTotalAmounts={clientData.manual_total_amount}
            requestedAmounts={clientData.requested_amount}
            hideInfo={true}
            allowCommitteeEdit={true}
            applications={clientData.products}
            onChange={(e) => setApplicationData(e)}
            clientData={clientData}
            signers={clientData.signers}
          />

          <Card margin={{ top: '3' }}>
            <TextArea
              placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
              maxLengthHelpText={false}
              onChange={(e) => setModalText(e.target.value)}
            ></TextArea>
          </Card>
        </Card>
      </Card>
    );
  };

  const multipleApprovalModal = () => {
    const nextStepCopy = committeeApprovalData
      ? buildCommitteeApprovalNextStepCopy({
          hasNext: committeeApprovalData.hasNext === true,
          pendingRoles: committeeApprovalData.roles || [],
          nextRoles: committeeApprovalData.nextRole || [],
        })
      : null;
    /**
     * Task 50111 — MEMBER_QUORUM: copy alternativa que explica el quórum.
     * Cuando esta aprobación llegue al `autoQuorum`, el backend empuja la
     * solicitud a INS automáticamente; cuando solo llegue al `minManualQuorum`,
     * queda habilitado el botón "Enviar a instrumentar" para cualquier miembro
     * que ya haya votado en el ciclo actual.
     */
    if (isMemberQuorumMode && quorum) {
      const remainingForAuto = Math.max(
        quorum.autoQuorum - quorum.approvalsCount - 1,
        0,
      );
      return (
        <Card>
          <P type='1' variant='neutral'>
            Está a punto de aprobar esta solicitud como miembro del comité.
          </P>
          <P type='1' variant='neutral' withMargin={false}>
            Aprobaciones actuales:{' '}
            <Span variant='neutral' weight='bold'>
              {quorum.approvalsCount} / {quorum.autoQuorum}
            </Span>
            . Mínimo manual:{' '}
            <Span variant='neutral' weight='bold'>{quorum.minManualQuorum}</Span>.
          </P>
          {remainingForAuto === 0 ? (
            <P type='1' variant='neutral' withMargin={false}>
              Con tu aprobación se alcanza el mínimo automático y la solicitud
              pasará a Instrumentación.
            </P>
          ) : (
            <P type='1' variant='neutral' withMargin={false}>
              Faltan {remainingForAuto} aprobación/es para que la solicitud pase
              automáticamente a Instrumentación.
            </P>
          )}
          <P type='1' variant='neutral'>
            <Span variant='neutral' weight='bold'>¿Desea continuar?</Span>
          </P>
          <Card margin={{ top: '3' }}>
            <TextArea
              label='Comentario (opcional)'
              placeHolder='Escribí un comentario (opcional)'
              maxLength={committeeCommentMaxLength}
              maxLengthHelpText={false}
              value={approvalDescription}
              onChange={(e) => setApprovalDescription(e.target.value)}
            />
          </Card>
        </Card>
      );
    }
    return (
      <Card>
        <P type='1' variant='neutral'>
          Está a punto de aprobar esta solicitud.
        </P>
        {nextStepCopy && (
          <P type='1' variant='neutral' withMargin={false}>
            {nextStepCopy}
          </P>
        )}
        <P type='1' variant='neutral'>
          <Span variant='neutral' weight='bold'>¿Desea continuar?</Span>
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            label='Comentario (opcional)'
            placeHolder='Escribí un comentario (opcional)'
            maxLength={committeeCommentMaxLength}
            maxLengthHelpText={false}
            value={approvalDescription}
            onChange={(e) => setApprovalDescription(e.target.value)}
          />
        </Card>
      </Card>
    );
  };

  /**
   * Task 50111 — MEMBER_QUORUM: confirma el envío manual a Instrumentación cuando
   * ya se alcanzó el mínimo manual. El backend valida la idempotencia y los
   * permisos (debe ser COMITE y haber votado en el ciclo actual).
   */
  const sendToInstrumentationModal = () => (
    <Card>
      <P type='1' variant='neutral'>
        Vas a enviar esta solicitud a Instrumentación con las aprobaciones
        actuales.
      </P>
      {quorum && (
        <P type='1' variant='neutral' withMargin={false}>
          Aprobaciones acumuladas:{' '}
          <Span variant='neutral' weight='bold'>
            {quorum.approvalsCount}
          </Span>{' '}
          (mínimo manual: {quorum.minManualQuorum}, automático:{' '}
          {quorum.autoQuorum}).
        </P>
      )}
      <P type='1' variant='neutral'>
        <Span variant='neutral' weight='bold'>¿Desea continuar?</Span>
      </P>
    </Card>
  );

  const handleSendToInstrumentation = () => {
    if (!cuit || !id) return;
    setIsSendingApplication(true);
    postSendCommitteeToInstrumentation(cuit, id)
      .then((response) => {
        if (isSuccessfulResponse(response)) {
          setCommitteeCreditoMaximoRefreshKey((n) => n + 1);
          refreshCommitteeMode();
          useToast.success({
            title: 'Solicitud enviada a Instrumentación.',
            id: 'application-toast',
            description: '',
            closeButton: false,
            autoClose: 5000,
          });
          setTimeout(() => {
            navigateAfterFlowAction(navigate, location.pathname, detailState);
          }, 1000);
        } else {
          useToast.error({
            title: 'No se pudo enviar la solicitud a Instrumentación.',
            id: 'application-toast',
            description: hasError(response).isClientError
              ? 'Verificá si el quórum mínimo está alcanzado.'
              : 'Error técnico',
            closeButton: true,
            autoClose: 5000,
          });
        }
      })
      .finally(() => setIsSendingApplication(false));
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Toast id='application-toast' />
      <RiskAnalysisPdf
        ref={pdfRef}
        clientData={committeeSolicitudClientData}
        riskData={riskData}
        balanceData={balanceInfo}
        postBalanceData={postBalanceData}
        debts={debts}
        documents={clientData.files}
        cuit={cuit}
        referrerNames={referrerNames}
        officers={officers}
        productLines={committeeProductLines}
        managements={managements}
        isManagementSelectionEnabled={isManagementSelectionEnabled}
        // Dual balance props (Cementos feature)
        showDualBalance={showDualBalance}
        previousBalanceData={previousBalanceInfo}
        previousBpbIndicators={previousBpbIndicators}
        sgrDistributions={clientData.sgr_distributions}
        isUruguayEnabled={isUruguayEnabled}
        localCurrencyCode={localCurrencyCode}
        creditoMaximoState={committeeCreditoState}
        creditoProductLines={committeeProductLines}
        creditoSuperaMaximo={committeeSuperaMaximo}
        isQuotationsEnabled={isQuotationsEnabled}
        creditoAvailableAssets={committeeAvailableAssets}
      />
      {canViewCommitteeFolder && (
        <>
          {(canPerformCommitteeDecline || canPerformCommitteeActions) &&
            !isRejected &&
            !isFinished && (
            <>
              {canPerformCommitteeDecline && (
              <DeclineOrRequestChangesModal
                open={isRejectApplication}
                onClose={() => {
                  setIsRejectApplication(false);
                  setModalText('');
                }}
                comment={modalText}
                onCommentChange={setModalText}
                selectionCount={1}
                requireComment={false}
                maxLength={committeeCommentMaxLength}
                hideDeclineOption={shouldHideCommitteeDeclineOption(
                  isMemberQuorumMode,
                  isAdmin,
                  isRiskAnalyst,
                )}
                onDecline={(comment) => handleReject(comment)}
                onRequestChanges={(comment) => handleRequestChanges(comment)}
              />
              )}
              {canPerformCommitteeActions && (
              <>
              <Modal
                width='1000px'
                isOpen={isSendApplicationModal}
                onClose={() => setIsSendApplicationModal(false)}
                title={'Aprobar solicitud'}
                primaryText='Aprobar solicitud'
                primaryVariant='primary'
                secondaryText='Cancelar'
                secondaryVariant='secondary'
                onSecondaryAction={() => {
                  setIsSendApplicationModal(false);
                }}
                onPrimaryAction={() => {
                  setIsSendApplicationModal(false);
                  handleApprove();
                }}
                children={sendApplicationModal()}
              />
              <Modal
                width='600px'
                isOpen={isMultipleApprovalModal}
                onClose={() => setIsMultipleApprovalModal(false)}
                title={'Confirmar aprobación'}
                primaryText='Confirmar aprobación'
                primaryVariant='primary'
                secondaryText='Cancelar'
                secondaryVariant='secondary'
                onSecondaryAction={() => {
                  setIsMultipleApprovalModal(false);
                }}
                onPrimaryAction={() => {
                  setIsMultipleApprovalModal(false);
                  handleMultipleApproval();
                }}
                children={multipleApprovalModal()}
              />
              <Modal
                width='600px'
                isOpen={isSendToInstrumentationModal}
                onClose={() => setIsSendToInstrumentationModal(false)}
                title={'Enviar a Instrumentación'}
                primaryText='Confirmar envío'
                primaryVariant='primary'
                secondaryText='Cancelar'
                secondaryVariant='secondary'
                onSecondaryAction={() => {
                  setIsSendToInstrumentationModal(false);
                }}
                onPrimaryAction={() => {
                  setIsSendToInstrumentationModal(false);
                  handleSendToInstrumentation();
                }}
                children={sendToInstrumentationModal()}
              />
              <MultiSgrApprovalModal
                open={isMultiSgrApprovalModal && isMultiSgrApprovalEnabled}
                onClose={() => setIsMultiSgrApprovalModal(false)}
                catalog={sgrCatalog}
                baseProducts={multiSgrBaseProducts}
                productLines={committeeProductLines}
                isInstrumentationEnabled={isInstrumentationEnabled}
                initialComment={approvalDescription}
                onSubmit={handleMultiSgrModalSubmit}
              />
              </>
              )}
            </>
          )}
          <Card display='flex' margin={{ left: '2', bottom: '4' }} gap='10px'>
            <Icon
              name='chevron_left_outline'
              height={24}
              width={24}
              cursor='pointer'
              onClick={handleGoBack}
            />
            <H withMargin={false} type='2' weight='bold' modifier='strong'>
              Solicitud de {capitalize(clientData?.basic_data?.company_name)} ({' '}
              {fiscalId.format(clientData.cuit)} )
            </H>
          </Card>
          {(() => {
            const isRiskTabDisabled = !hasCommitteeRiskTabContent(riskData, {
              isUruguayEnabled,
            });
            return (
              <Tabs
                tabs={contentTabs}
                disabledTabs={isRiskTabDisabled ? [1] : []}
              />
            );
          })()}
          <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
            {!isRejected && !isFinished && shouldShowDeclineButtons && canPerformCommitteeDecline && (
              <Card width='280px'>
                <Button
                  id='application-button-reject'
                  size='lg'
                  variant='secondary'
                  onClick={() => setIsRejectApplication(true)}
                  fullWidth
                  disabled={isSendingApplication}
                >
                  {isSendingApplication ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Declinar o solicitar cambios'
                  )}
                </Button>
              </Card>
            )}
            {/**
             * Task 50111 — MEMBER_QUORUM: botón "Enviar a Instrumentación" visible
             * sólo si el backend lo habilita (`canSendToInstrumentation`). Importante:
             * NO se renderiza junto al botón Aprobar para el mismo user en el mismo
             * ciclo, ya que `canSendToInstrumentation` requiere que el user haya
             * aprobado primero (`userAlreadyApproved=true`).
             */}
            {!isRejected && !isFinished && canSendToInstrumentation && (
              <Card width='220px' margin={{ left: '4' }}>
                <Button
                  id='committee-button-send-to-instrumentation'
                  size='lg'
                  variant='primary'
                  onClick={() => setIsSendToInstrumentationModal(true)}
                  fullWidth
                  disabled={isSendingApplication}
                >
                  {isSendingApplication ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Enviar a Instrumentación'
                  )}
                </Button>
              </Card>
            )}
            {!isRejected && !isFinished && shouldShowApprovalButtons && canPerformCommitteeActions && (
              <Card width='180px' margin={{ left: '4' }}>
                <Button
                  id='client-button-approve'
                  size='lg'
                  variant='primary'
                  onClick={() => {
                    if (
                      isMultipleApprovalInCommitteeEnabled ||
                      isMemberQuorumMode
                    ) {
                      setIsMultipleApprovalModal(true);
                    } else if (isMultiSgrApprovalEnabled) {
                      if (sgrCatalog.length > 0) {
                        setIsMultiSgrApprovalModal(true);
                        return;
                      }
                      setIsSendingApplication(true);
                      getSgrCatalog()
                        .then((catResponse) => {
                          if (
                            hasData(catResponse) &&
                            catResponse.status === 200 &&
                            Array.isArray(catResponse.data)
                          ) {
                            setSgrCatalog(catResponse.data as SgrCatalogItem[]);
                            setIsMultiSgrApprovalModal(true);
                          } else {
                            setIsSendApplicationModal(true);
                          }
                        })
                        .catch(() => {
                          setIsSendApplicationModal(true);
                        })
                        .finally(() => {
                          setIsSendingApplication(false);
                        });
                    } else {
                      setIsSendApplicationModal(true);
                    }
                  }}
                  fullWidth
                  disabled={isSendingApplication}
              >
                {isSendingApplication ? (
                  <Spinner size='sm' variant='primary' />
                ) : (
                  'Aprobar'
                )}
              </Button>
            </Card>
            )}
          </Card>
        </>
      )}
    </Card>
  );
};

export default Committee;
